import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdCampaign, MdLogin } from 'react-icons/md';
import { usePppApi } from '../services/pppApi';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

function scadenzaInfo(termineProposte) {
  if (!termineProposte) return null;
  const giorni = Math.round((new Date(termineProposte) - new Date()) / 86400000);
  if (giorni < 0)  return { label: `Scaduto da ${Math.abs(giorni)} giorni`, cls: 'text-red-600' };
  if (giorni <= 7) return { label: `Scade tra ${giorni} giorni`, cls: 'text-red-600' };
  if (giorni <= 15)return { label: `Scade tra ${giorni} giorni`, cls: 'text-amber-600' };
  return               { label: `${giorni} giorni alla scadenza`, cls: 'text-blue-600' };
}

export default function AvvisiPublicPage() {
  const api = usePppApi();
  const navigate = useNavigate();
  const [avvisi, setAvvisi]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listAvvisiPublic({
      onSuccess: (data) => { setAvvisi(Array.isArray(data) ? data : []); setLoading(false); },
      onError: () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MdCampaign size={28} className="text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Avvisi Pubblici PPP</h1>
            <p className="text-xs text-gray-500">Art. 193 D.lgs 36/2023 — Partenariato Pubblico Privato</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <MdLogin size={16} /> Accedi per partecipare
        </button>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Caricamento...</div>
        ) : avvisi.length === 0 ? (
          <div className="text-center py-16">
            <MdCampaign size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Nessun avviso pubblico disponibile al momento.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">
              {avvisi.length} avviso{avvisi.length !== 1 ? 'i' : ''} pubblicato{avvisi.length !== 1 ? 'i' : ''}
            </p>
            {avvisi.map((avviso) => {
              const scadenza = scadenzaInfo(avviso.terminePresentazioneProposte);
              return (
                <div
                  key={avviso.id}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          Pubblicato
                        </span>
                        {avviso.sopraSogliaEuropea && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            Sopra soglia UE
                          </span>
                        )}
                        {avviso.versione > 1 && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            v{avviso.versione}
                          </span>
                        )}
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">{avviso.oggetto}</h2>
                      {avviso.procedimentoTitolo && (
                        <p className="text-sm text-gray-500 mb-3">{avviso.procedimentoTitolo}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                        <span>Pubblicato: {formatDate(avviso.dataPubblicazione)}</span>
                        <span>Termine: {formatDate(avviso.terminePresentazioneProposte)}</span>
                        {scadenza && (
                          <span className={`font-medium ${scadenza.cls}`}>{scadenza.label}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => navigate(`/avvisi/${avviso.uuid ?? avviso.id}`)}
                        className="text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                      >
                        Dettaglio →
                      </button>
                      <button
                        onClick={() => navigate('/login')}
                        className="text-sm bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Accedi
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer istituzionale */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">
            Procedura di Partenariato Pubblico Privato ex art. 193 D.lgs 36/2023 (Codice dei Contratti Pubblici)
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Per partecipare è necessario{' '}
            <button onClick={() => navigate('/login')} className="text-blue-600 hover:underline">
              accedere tramite SPID
            </button>{' '}
            e registrare la propria azienda nel portale.
          </p>
        </div>
      </main>
    </div>
  );
}
